export type OpenRouterRole = 'assistant' | 'system' | 'user'

export interface OpenRouterMessage {
  content: string
  role: OpenRouterRole
}

export interface OpenRouterRequest {
  apiKey: string
  messages: OpenRouterMessage[]
  model: string
}

interface OpenRouterErrorPayload {
  error?: {
    message?: string
  }
}

interface OpenRouterContentPart {
  text?: string
  type?: string
}

type OpenRouterContent = OpenRouterContentPart[] | string

interface OpenRouterResponsePayload extends OpenRouterErrorPayload {
  choices?: Array<{
    message?: {
      content?: OpenRouterContent
    }
  }>
}

function normalizeContent(content: OpenRouterContent | undefined): string | null {
  if (typeof content === 'string') {
    const text = content.trim()
    return text.length > 0 ? text : null
  }

  if (!Array.isArray(content)) {
    return null
  }

  const merged = content
    .map((part) => (typeof part.text === 'string' ? part.text : ''))
    .join('')
    .trim()

  return merged.length > 0 ? merged : null
}

function normalizeApiKey(rawApiKey: string): string {
  return rawApiKey.trim().replace(/^Bearer\s+/i, '')
}

function resolveOpenRouterError(response: Response, payload: OpenRouterErrorPayload | null): Error {
  const providerMessage = payload?.error?.message?.trim()
  if (providerMessage) {
    return new Error(
      `[OPENROUTER_HTTP_ERROR] OpenRouter request failed with status ${response.status}: ${providerMessage}`
    )
  }

  if (response.status === 401) {
    return new Error(
      '[OPENROUTER_HTTP_ERROR] OpenRouter request failed with status 401. Verify your OpenRouter API key is valid and entered without the "Bearer " prefix.'
    )
  }

  return new Error(`[OPENROUTER_HTTP_ERROR] OpenRouter request failed with status ${response.status}.`)
}

export async function requestOpenRouterCompletion({
  apiKey,
  messages,
  model,
}: OpenRouterRequest): Promise<string> {
  const normalizedApiKey = normalizeApiKey(apiKey)
  if (!normalizedApiKey) {
    throw new Error('[OPENROUTER_NOT_CONFIGURED] OpenRouter API key is required.')
  }

  const referer = globalThis.location?.origin?.trim()
  if (!referer) {
    throw new Error('[OPENROUTER_REFERER_UNAVAILABLE] Browser origin is unavailable for OpenRouter requests.')
  }

  let response: Response

  try {
    response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${normalizedApiKey}`,
        'Content-Type': 'application/json',
        'HTTP-Referer': referer,
        'X-Title': 'Alberto Health',
      },
      body: JSON.stringify({
        model,
        messages,
        temperature: 0.2,
      }),
    })
  } catch {
    throw new Error('[OPENROUTER_NETWORK_ERROR] Network request to OpenRouter failed.')
  }

  let payload: OpenRouterResponsePayload | null = null
  try {
    payload = (await response.json()) as OpenRouterResponsePayload
  } catch {
    if (!response.ok) {
      throw resolveOpenRouterError(response, null)
    }

    throw new Error('[OPENROUTER_INVALID_JSON] OpenRouter returned an invalid JSON payload.')
  }

  if (!response.ok) {
    throw resolveOpenRouterError(response, payload)
  }

  const content = normalizeContent(payload.choices?.[0]?.message?.content)
  if (!content) {
    throw new Error('[OPENROUTER_EMPTY_RESPONSE] OpenRouter returned an empty response.')
  }

  return content
}
